import type { MdySubscription } from "@hz/common/monday/subscription.types";

import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { Flex, Heading, HStack, Text, VStack } from "@chakra-ui/react";

import { Alert } from "@components/ui/alert";
import { Button } from "@components/ui/button";
import { StepsCompletedContent } from "@components/ui/steps";

import { fetchInit, selectMondayState } from "@state/mondaySlice";
import type { AppDispatch } from "@state/store";

import { openPlanSelection } from "@utils/monday-plans";

const SetupCompleteStage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const { isTrial, subscription, isLoading } = useSelector(selectMondayState);

  const handleOpenBoardView = async () => {
    // Gets latest stage, updates state with it
    await dispatch(fetchInit({ forceRefetch: true }));
    navigate("/", { replace: true });
  };

  return (
    <StepsCompletedContent
      display="flex"
      alignItems="center"
      justifyContent="center"
      w="full"
    >
      <VStack
        gap={0}
        bg="white"
        p={6}
        borderRadius="lg"
        shadow="xs"
        align="flex-start"
        w={{
          base: "full",
          lg: "752px",
        }}
        maxW="752px"
      >
        {isTrial ? (
          <OnTrialMessage
            handleOpenBoardView={handleOpenBoardView}
            subscription={subscription!}
            openPlanSelection={openPlanSelection}
            isLoading={isLoading}
          />
        ) : (
          <PayingContactMessage
            handleOpenBoardView={handleOpenBoardView}
            isLoading={isLoading}
          />
        )}
      </VStack>
    </StepsCompletedContent>
  );
};

export default SetupCompleteStage;

type OnTrialMessageProps = {
  handleOpenBoardView: () => void;
  openPlanSelection: () => void;
  subscription: MdySubscription;
  isLoading: boolean;
};

const OnTrialMessage = ({
  handleOpenBoardView,
  subscription,
  openPlanSelection,
  isLoading,
}: OnTrialMessageProps) => (
  <>
    <Flex
      gap={3}
      mb={4}
      align="center"
    >
      <Text fontSize="3xl">🎉</Text>
      <Heading
        as="h2"
        size="md"
        color="gray.900"
      >
        Setup Complete! - Your {subscription.days_left}-Day Free Trial is Active!
      </Heading>
    </Flex>
    <VStack
      align="stretch"
      color="gray.600"
    >
      <Text fontSize="sm">
        Your QuickBooks records are now syncing with this monday board. The initial sync may take up to 2
        hours to complete.
      </Text>
      <Text fontSize="sm">
        While you wait, feel free to start managing your QuickBooks records directly in the Board View.
      </Text>
      <Text fontSize="sm">To connect other QuickBooks records, visit your other monday boards.</Text>
    </VStack>

    <Alert
      showIcon={false}
      bg="blue.50"
      mt={6}
      rounded="lg"
      border="1px"
      borderColor="blue"
      fontWeight={600}
    >
      Your 14-day trial gives you full access to all features. Subscribe now to ensure uninterrupted access
      after your trial.
    </Alert>

    <HStack
      justifyContent="flex-end"
      width="100%"
      mt={8}
    >
      <Button
        variant="ghost"
        colorPalette="blue"
        onClick={handleOpenBoardView}
        loading={isLoading}
      >
        Open board view
      </Button>
      <Button
        colorPalette="blue"
        onClick={openPlanSelection}
      >
        Subscribe now
      </Button>
    </HStack>
  </>
);

type PayingContactMessageProps = {
  handleOpenBoardView: () => void;
  isLoading: boolean;
};

const PayingContactMessage = ({ handleOpenBoardView, isLoading }: PayingContactMessageProps) => (
  <>
    <Flex
      gap={3}
      mb={4}
      align="center"
    >
      <Text fontSize="3xl">🎉</Text>
      <Heading
        as="h2"
        size="md"
        color="gray.900"
      >
        Setup Complete!
      </Heading>
    </Flex>

    <HStack
      justifyContent="flex-end"
      width="100%"
      mt={8}
    >
      <Button
        colorPalette="blue"
        onClick={handleOpenBoardView}
        loading={isLoading}
      >
        Open Dashboard
      </Button>
    </HStack>
  </>
);
